import { Grid, TextField, Button, Snackbar, Alert, CircularProgress, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { register, clearAuthError } from "../../../Redux/Auth/Action";
import { useEffect, useState } from "react";

// Email validation regex
const EMAIL_REGEX = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Password validation regex: min 8 chars, 1 uppercase, 1 special character
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

export default function RegisterUserForm({ handleNext, onSwitchToLogin, onFieldsChange }) {
  const dispatch = useDispatch();
  const { auth } = useSelector((store) => store);

  // Form fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Notify parent of field changes
  useEffect(() => {
    if (onFieldsChange) {
      const isDirty = !!(firstName.trim() || lastName.trim() || email.trim() || password.trim() || confirmPassword.trim());
      onFieldsChange(isDirty);
    }
  }, [firstName, lastName, email, password, confirmPassword, onFieldsChange]);


  // UI states
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Auto-populate form fields from checkout state (when coming from checkout flow)
  useEffect(() => {
    const checkoutStateStr = sessionStorage.getItem("checkoutState");
    if (checkoutStateStr) {
      try {
        const checkoutState = JSON.parse(checkoutStateStr);

        // Populate email if available
        if (checkoutState.email && !email) {
          setEmail(checkoutState.email);
        }

        // Populate name from address if available
        if (checkoutState.address) {
          if (checkoutState.address.firstName && !firstName) {
            setFirstName(checkoutState.address.firstName);
          }
          if (checkoutState.address.lastName && !lastName) {
            setLastName(checkoutState.address.lastName);
          }
        }
      } catch (error) {
        console.error("Error parsing checkout state for auto-populate:", error);
      }
    }
  }, [email, firstName, lastName]); // Run only on mount initially or when populated

  // Handle successful registration
  useEffect(() => {
    if (auth.registerSuccess) {
      setRegistrationSuccess(true);
      setSnackbarMessage("Registration successful! Please login with your credentials.");
      setSnackbarSeverity("success");
      setOpenSnackBar(true);
    }
  }, [auth.registerSuccess]);

  // Handle errors
  useEffect(() => {
    if (auth.error) {
      setSnackbarMessage(auth.error);
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
    }
  }, [auth.error]);

  const handleClose = () => {
    setOpenSnackBar(false);
    dispatch(clearAuthError());
  };

  // Validate email format
  const validateEmail = (emailValue) => {
    if (!emailValue) {
      setEmailError("");
      return true;
    }
    if (!EMAIL_REGEX.test(emailValue)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  // Validate password format
  const validatePassword = (passwordValue) => {
    if (!passwordValue) {
      setPasswordError("");
      return true;
    }
    if (!PASSWORD_REGEX.test(passwordValue)) {
      setPasswordError("Min 8 characters, 1 uppercase, 1 special character");
      return false;
    }
    setPasswordError("");
    return true;
  };

  // Validate confirm password matches
  const validateConfirmPassword = (confirmValue) => {
    if (!confirmValue) {
      setConfirmPasswordError("");
      return true;
    }
    if (confirmValue !== password) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  // Submit registration form
  const handleFormSubmit = async (event) => {
    event.preventDefault();

    // Validate required fields
    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setSnackbarMessage("Please fill in all fields");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    if (!validateEmail(email)) {
      setSnackbarMessage("Please enter a valid email address");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    if (!validatePassword(password)) {
      setSnackbarMessage("Please fix the password errors");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      setSnackbarMessage("Passwords do not match");
      setSnackbarSeverity("error");
      setOpenSnackBar(true);
      return;
    }

    // Register with user-provided password
    const userData = {
      firstName,
      lastName,
      email,
      password
    };

    dispatch(register(userData));
  };

  // Show success screen after registration
  if (registrationSuccess) {
    return (
      <div className="">
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" color="success.main" gutterBottom>
            ✓ Registration Successful!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Your account has been created with <strong>{email}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please login with your email and password.
          </Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <strong>Next Steps:</strong>
            <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
              <li>Switch to the Login tab above</li>
              <li>Enter your email and password</li>
              <li>Complete OTP verification</li>
              <li>You'll be redirected to continue your order</li>
            </ol>
          </Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (onSwitchToLogin) {
                onSwitchToLogin();
              } else {
                window.location.reload();
              }
            }}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Box>

        <Snackbar
          open={openSnackBar}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  }

  // Registration Form with password fields
  return (
    <div className="">
      {auth.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {auth.error}
        </Alert>
      )}

      <form onSubmit={handleFormSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="firstName"
              name="firstName"
              label="First Name"
              fullWidth
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="lastName"
              name="lastName"
              label="Last Name"
              fullWidth
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="email"
              name="email"
              label="Email"
              fullWidth
              autoComplete="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) validateEmail(e.target.value);
              }}
              onBlur={(e) => validateEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="password"
              name="password"
              label="Password"
              fullWidth
              autoComplete="new-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) validatePassword(e.target.value);
                if (confirmPassword) validateConfirmPassword(confirmPassword);
              }}
              onBlur={(e) => validatePassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError || "Min 8 characters, 1 uppercase, 1 special character"}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              fullWidth
              autoComplete="new-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmPasswordError) validateConfirmPassword(e.target.value);
              }}
              onBlur={(e) => validateConfirmPassword(e.target.value)}
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              className="bg-[#9155FD] w-full"
              type="submit"
              variant="contained"
              size="large"
              sx={{ padding: ".8rem 0" }}
              disabled={auth.isLoading || !!passwordError || !!confirmPasswordError}
            >
              {auth.isLoading ? <CircularProgress size={24} color="inherit" /> : "Register"}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={openSnackBar}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}
